import * as THREE from 'three';
import type { PieceKind, SpecialType } from '../core/types';
import { PIECE_PALETTES, SPECIAL_COLORS } from './palette';

const geoCache = new Map<string, THREE.BufferGeometry>();
const matCache = new Map<string, THREE.MeshStandardMaterial>();

function cacheGeo(key: string, factory: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let g = geoCache.get(key);
  if (!g) {
    g = factory();
    geoCache.set(key, g);
  }
  return g;
}

function matFor(
  kind: PieceKind,
  special?: SpecialType,
  colorblind = false,
  highContrast = false
): THREE.MeshStandardMaterial {
  const key = `${kind}:${special ?? 'n'}:${colorblind}:${highContrast}`;
  let m = matCache.get(key);
  if (m) return m;
  const pal = PIECE_PALETTES[kind];
  const color = special ? SPECIAL_COLORS[special] : pal.primary;
  m = new THREE.MeshStandardMaterial({
    color: highContrast ? color : pal.primary,
    emissive: new THREE.Color(special ? SPECIAL_COLORS[special] : pal.emissive),
    emissiveIntensity: special ? 0.45 : 0.22,
    roughness: pal.roughness,
    metalness: pal.metalness,
    flatShading: false,
  });
  if (special) {
    m.color = new THREE.Color(pal.primary).lerp(new THREE.Color(SPECIAL_COLORS[special]), 0.35);
  }
  matCache.set(key, m);
  return m;
}

/** Build a unique stylized mesh for each essence kind. */
export function createPieceMesh(
  kind: PieceKind,
  special?: SpecialType,
  opts: { colorblind?: boolean; highContrast?: boolean; beamAxis?: 'h' | 'v' } = {}
): THREE.Group {
  const group = new THREE.Group();
  group.name = `piece_${kind}${special ? '_' + special : ''}`;

  const body = buildBody(kind);
  const material = matFor(kind, special, opts.colorblind, opts.highContrast);
  body.material = material;
  body.castShadow = true;
  body.receiveShadow = true;
  body.scale.setScalar(1.15);
  group.add(body);

  // Accent rim light shell
  const shell = body.clone();
  shell.scale.multiplyScalar(1.06);
  shell.material = new THREE.MeshBasicMaterial({
    color: PIECE_PALETTES[kind].accent,
    transparent: true,
    opacity: 0.12,
    depthWrite: false,
  });
  group.add(shell);

  if (special) {
    group.add(buildSpecialOverlay(special, opts.beamAxis));
  }

  // Colorblind pattern disc
  if (opts.colorblind) {
    group.add(buildPatternBadge(kind));
  }

  group.userData = { kind, special, baseY: 0 };
  return group;
}

function buildBody(kind: PieceKind): THREE.Mesh {
  switch (kind) {
    case 'ember':
      return new THREE.Mesh(
        cacheGeo('ember', () => {
          // Flame petal: elongated teardrop via lathe
          const pts: THREE.Vector2[] = [];
          for (let i = 0; i <= 12; i++) {
            const t = i / 12;
            const x = Math.sin(t * Math.PI) * 0.28 * (1 - t * 0.35);
            const y = (t - 0.5) * 0.9;
            pts.push(new THREE.Vector2(x, y));
          }
          const g = new THREE.LatheGeometry(pts, 16);
          g.rotateX(Math.PI / 2);
          return g;
        })
      );
    case 'tide':
      return new THREE.Mesh(
        cacheGeo('tide', () => {
          // Spiral shell-ish torus knot
          const g = new THREE.TorusKnotGeometry(0.28, 0.1, 64, 12, 2, 3);
          return g;
        })
      );
    case 'gale':
      return new THREE.Mesh(
        cacheGeo('gale', () => {
          // Feather: flat tapered wing from shape
          const shape = new THREE.Shape();
          shape.moveTo(0, -0.4);
          shape.quadraticCurveTo(0.35, -0.1, 0.15, 0.4);
          shape.quadraticCurveTo(0, 0.25, -0.15, 0.4);
          shape.quadraticCurveTo(-0.35, -0.1, 0, -0.4);
          const g = new THREE.ExtrudeGeometry(shape, {
            depth: 0.12,
            bevelEnabled: true,
            bevelThickness: 0.03,
            bevelSize: 0.03,
            bevelSegments: 2,
          });
          g.center();
          return g;
        })
      );
    case 'root':
      return new THREE.Mesh(
        cacheGeo('root', () => {
          // Knotted cross of tubes
          const g = new THREE.TorusKnotGeometry(0.26, 0.09, 48, 10, 3, 2);
          return g;
        })
      );
    case 'solar':
      return new THREE.Mesh(
        cacheGeo('solar', () => {
          // Multi-point crest
          const shape = new THREE.Shape();
          const spikes = 8;
          for (let i = 0; i < spikes * 2; i++) {
            const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
            const r = i % 2 === 0 ? 0.42 : 0.2;
            const x = Math.cos(a) * r;
            const y = Math.sin(a) * r;
            if (i === 0) shape.moveTo(x, y);
            else shape.lineTo(x, y);
          }
          shape.closePath();
          const g = new THREE.ExtrudeGeometry(shape, {
            depth: 0.14,
            bevelEnabled: true,
            bevelThickness: 0.03,
            bevelSize: 0.025,
            bevelSegments: 2,
          });
          g.center();
          return g;
        })
      );
    case 'moon':
      return new THREE.Mesh(
        cacheGeo('moon', () => {
          // Crescent via CSG-like: we approximate with torus section + sphere
          // Use a ring with thick profile
          const g = new THREE.TorusGeometry(0.28, 0.12, 12, 24, Math.PI * 1.4);
          g.rotateY(Math.PI / 2);
          return g;
        })
      );
    case 'void':
      return new THREE.Mesh(
        cacheGeo('void', () => {
          // Hollow octahedron dual — faceted prism
          const g = new THREE.OctahedronGeometry(0.4, 0);
          return g;
        })
      );
  }
}

function buildSpecialOverlay(special: SpecialType, beamAxis?: 'h' | 'v'): THREE.Object3D {
  const g = new THREE.Group();
  const color = SPECIAL_COLORS[special];
  const glowMat = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.8,
    roughness: 0.25,
    metalness: 0.5,
    transparent: true,
    opacity: 0.9,
  });

  switch (special) {
    case 'beam': {
      const bar = new THREE.Mesh(
        cacheGeo('beam_bar', () => new THREE.CapsuleGeometry(0.06, 0.7, 4, 8)),
        glowMat
      );
      if (beamAxis === 'v') bar.rotation.z = Math.PI / 2;
      else bar.rotation.z = 0;
      // default horizontal capsule is along Y; rotate for axis
      bar.rotation.z = beamAxis === 'v' ? 0 : Math.PI / 2;
      g.add(bar);
      break;
    }
    case 'nova': {
      const core = new THREE.Mesh(
        cacheGeo('nova_core', () => new THREE.IcosahedronGeometry(0.18, 0)),
        glowMat
      );
      g.add(core);
      for (let i = 0; i < 6; i++) {
        const ray = new THREE.Mesh(
          cacheGeo('nova_ray', () => new THREE.ConeGeometry(0.05, 0.28, 6)),
          glowMat
        );
        const a = (i / 6) * Math.PI * 2;
        ray.position.set(Math.cos(a) * 0.32, Math.sin(a) * 0.32, 0);
        ray.rotation.z = a - Math.PI / 2;
        g.add(ray);
      }
      break;
    }
    case 'prism': {
      const heart = new THREE.Mesh(
        cacheGeo('prism_heart', () => new THREE.DodecahedronGeometry(0.22, 0)),
        glowMat
      );
      g.add(heart);
      const ring = new THREE.Mesh(
        cacheGeo('prism_ring', () => new THREE.TorusGeometry(0.32, 0.03, 8, 24)),
        glowMat
      );
      g.add(ring);
      break;
    }
    case 'burst': {
      const cocoon = new THREE.Mesh(
        cacheGeo('burst_cocoon', () => new THREE.SphereGeometry(0.22, 12, 10)),
        glowMat
      );
      cocoon.scale.set(1, 1.15, 0.85);
      g.add(cocoon);
      const wrap = new THREE.Mesh(
        cacheGeo('burst_wrap', () => new THREE.TorusGeometry(0.24, 0.04, 6, 16)),
        glowMat
      );
      wrap.rotation.x = Math.PI / 2;
      g.add(wrap);
      break;
    }
  }
  g.position.z = 0.15;
  return g;
}

function buildPatternBadge(kind: PieceKind): THREE.Mesh {
  const pattern = PIECE_PALETTES[kind].pattern;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, 64, 64);
  ctx.fillStyle = '#111111';
  ctx.strokeStyle = '#111111';
  ctx.lineWidth = 4;
  switch (pattern) {
    case 'stripes':
      for (let i = 0; i < 6; i++) ctx.fillRect(i * 12, 0, 5, 64);
      break;
    case 'dots':
      for (let y = 0; y < 4; y++)
        for (let x = 0; x < 4; x++) {
          ctx.beginPath();
          ctx.arc(10 + x * 14, 10 + y * 14, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      break;
    case 'rings':
      ctx.beginPath();
      ctx.arc(32, 32, 20, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(32, 32, 10, 0, Math.PI * 2);
      ctx.stroke();
      break;
    case 'chevrons':
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(8, 8 + i * 14);
        ctx.lineTo(32, 20 + i * 14);
        ctx.lineTo(56, 8 + i * 14);
        ctx.stroke();
      }
      break;
    case 'cross':
      ctx.fillRect(28, 8, 8, 48);
      ctx.fillRect(8, 28, 48, 8);
      break;
    case 'waves':
      for (let i = 0; i < 4; i++) {
        ctx.beginPath();
        ctx.moveTo(4, 12 + i * 14);
        ctx.bezierCurveTo(20, 0 + i * 14, 44, 24 + i * 14, 60, 12 + i * 14);
        ctx.stroke();
      }
      break;
    case 'diamonds':
      for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
          const cx = 14 + j * 18;
          const cy = 14 + i * 18;
          ctx.beginPath();
          ctx.moveTo(cx, cy - 6);
          ctx.lineTo(cx + 6, cy);
          ctx.lineTo(cx, cy + 6);
          ctx.lineTo(cx - 6, cy);
          ctx.closePath();
          ctx.fill();
        }
      }
      break;
  }
  const tex = new THREE.CanvasTexture(canvas);
  const mesh = new THREE.Mesh(
    new THREE.CircleGeometry(0.14, 16),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true })
  );
  mesh.position.set(0.28, 0.28, 0.35);
  return mesh;
}

export function createRelicMesh(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffe08a,
    emissive: 0xffaa33,
    emissiveIntensity: 0.6,
    metalness: 0.7,
    roughness: 0.25,
  });
  const star = new THREE.Mesh(
    cacheGeo('relic_star', () => {
      const shape = new THREE.Shape();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 === 0 ? 0.22 : 0.1;
        const x = Math.cos(a) * r;
        const y = Math.sin(a) * r;
        if (i === 0) shape.moveTo(x, y);
        else shape.lineTo(x, y);
      }
      shape.closePath();
      const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.08, bevelEnabled: true, bevelThickness: 0.02, bevelSize: 0.02, bevelSegments: 1 });
      geo.center();
      return geo;
    }),
    mat
  );
  g.add(star);
  g.position.z = 0.4;
  return g;
}

export function createBlockerMesh(
  type: string,
  layers: number
): THREE.Group {
  const g = new THREE.Group();
  const opacity = 0.35 + layers * 0.15;
  let color = 0x88ccee;
  let geo: THREE.BufferGeometry = new THREE.BoxGeometry(0.85, 0.85, 0.2);

  switch (type) {
    case 'frost':
      color = 0xa8e6ff;
      geo = new THREE.BoxGeometry(0.88, 0.88, 0.12 + layers * 0.06);
      break;
    case 'crate':
      color = 0x8b5a2b;
      geo = new THREE.BoxGeometry(0.9, 0.9, 0.5);
      break;
    case 'vine':
      color = 0x3d8b4a;
      geo = new THREE.TorusKnotGeometry(0.28, 0.08, 40, 8, 2, 3);
      break;
    case 'seal':
      color = 0xc0a060;
      geo = new THREE.CylinderGeometry(0.35, 0.35, 0.15, 6);
      break;
    case 'rift':
      color = 0x9b30ff;
      geo = new THREE.OctahedronGeometry(0.35, 0);
      break;
  }

  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      color,
      transparent: true,
      opacity: Math.min(0.95, opacity),
      roughness: 0.5,
      metalness: 0.2,
      emissive: type === 'rift' ? 0x5500aa : 0x000000,
      emissiveIntensity: type === 'rift' ? 0.5 : 0,
    })
  );
  g.add(mesh);
  if (type === 'crate') {
    // wood cross detail
    const bar = new THREE.Mesh(
      new THREE.BoxGeometry(0.1, 0.85, 0.52),
      new THREE.MeshStandardMaterial({ color: 0x5a3a18 })
    );
    g.add(bar);
    const bar2 = bar.clone();
    bar2.rotation.z = Math.PI / 2;
    g.add(bar2);
  }
  return g;
}

export function disposePieceCaches(): void {
  for (const g of geoCache.values()) g.dispose();
  for (const m of matCache.values()) m.dispose();
  geoCache.clear();
  matCache.clear();
}
